import React from 'react';
import HeroSection from '../components/HeroSection';
import { Globe, Zap, Users, TrendingUp, Award, Target } from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: <Globe size={32} />,
      title: 'Global Reach',
      description: 'Expand your business worldwide with our digital solutions'
    },
    {
      icon: <Zap size={32} />,
      title: 'Fast & Efficient',
      description: 'Quick turnaround times without compromising quality'
    },
    {
      icon: <Users size={32} />,
      title: 'Expert Team',
      description: 'Seasoned professionals dedicated to your success'
    }
  ];

  const achievements = [
    {
      icon: <Award size={48} />,
      number: '10+',
      label: 'Successful Projects',
      color: '#33cccc'
    },
    {
      icon: <Target size={48} />,
      number: '100%',
      label: 'Client Satisfaction',
      color: '#244359'
    },
    {
      icon: <TrendingUp size={48} />,
      number: '24/7',
      label: 'Support Available',
      color: '#33cccc'
    }
  ];

  return (
    <div>
      <HeroSection />
      
      {/* Why Choose Us Section */}
      <section style={{ background: 'white', padding: '5rem 1.5rem' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '700px', margin: '0 auto 3rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Why Choose ibTECON?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              We combine innovation, expertise, and dedication to deliver exceptional results
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, rgba(51, 204, 204, 0.05) 0%, rgba(218, 229, 239, 0.1) 100%)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '16px',
                  padding: '2.5rem 2rem',
                  textAlign: 'center',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(51, 204, 204, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'var(--gradient-button)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    color: 'white'
                  }}
                >
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                  {feature.title}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section with Visual Impact */}
      <section style={{ background: 'var(--bg-section)', padding: '5rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative circles */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(51, 204, 204, 0.1) 0%, transparent 70%)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-150px',
          left: '-150px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(36, 67, 89, 0.05) 0%, transparent 70%)',
          zIndex: 0
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Our Achievements
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Numbers that speak for our commitment to excellence
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '3rem' }}>
            {achievements.map((achievement, index) => (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  padding: '3rem 2rem',
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(51, 204, 204, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                }}
              >
                <div style={{ color: achievement.color, marginBottom: '1rem' }}>
                  {achievement.icon}
                </div>
                <div style={{ fontSize: '3.5rem', fontWeight: '700', color: achievement.color, marginBottom: '0.5rem', lineHeight: '1' }}>
                  {achievement.number}
                </div>
                <div className="body-medium" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  {achievement.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ background: 'linear-gradient(135deg, #33cccc 0%, #2bb3b3 100%)', padding: '5rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
        {/* Animated background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="heading-2" style={{ color: 'white', marginBottom: '1rem' }}>
            Ready to Transform Your Digital Presence?
          </h2>
          <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.95)', marginBottom: '2.5rem' }}>
            Let's work together to create something amazing. Our team is ready to bring your vision to life.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/contact"
              style={{
                background: 'white',
                color: '#33cccc',
                border: 'none',
                borderRadius: '9999px',
                padding: '16px 32px',
                fontFamily: 'system-ui, sans-serif',
                fontWeight: '600',
                fontSize: '1.125rem',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
              }}
            >
              Get Started Today
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;